import type { ComponentProps, ReactNode } from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { cn } from '@/lib/utils'

/**
 * Form primitives shared by the CMS and the public contact form. Inputs sit on
 * the raised ink surface with a hairline border that turns blood red on focus —
 * the same visual grammar as the rest of the site.
 */

const fieldBase =
  'w-full bg-ink-raised border border-hairline px-3 py-2.5 text-[0.9375rem] text-bone placeholder:text-bone-faint transition-colors focus:border-blood focus:outline-none disabled:opacity-50 aria-[invalid=true]:border-blood'

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(fieldBase, className)} {...props} />
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(fieldBase, 'min-h-28 resize-y leading-relaxed', className)}
      {...props}
    />
  )
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        fieldBase,
        // Native select keeps keyboard behaviour and mobile pickers for free.
        "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%239d978b' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")] appearance-none bg-[length:12px] bg-[right_0.85rem_center] bg-no-repeat pr-9",
        className
      )}
      {...props}
    >
      {children}
    </select>
  )
}

export function Label({ className, ...props }: ComponentProps<'label'>) {
  return (
    <label
      className={cn(
        'condensed text-bone-dim block text-[0.6875rem] font-semibold tracking-[0.18em]',
        className
      )}
      {...props}
    />
  )
}

interface FieldProps {
  label?: string
  htmlFor?: string
  hint?: string
  error?: string
  required?: boolean
  className?: string
  children: ReactNode
}

export function Field({ label, htmlFor, hint, error, required, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {label ? (
        <Label htmlFor={htmlFor}>
          {label}
          {required ? <span className="text-blood ml-1">*</span> : null}
        </Label>
      ) : null}
      {children}
      {error ? (
        <p className="text-blood-bright text-[0.8125rem]">{error}</p>
      ) : hint ? (
        <p className="text-bone-faint text-[0.8125rem] leading-snug">{hint}</p>
      ) : null}
    </div>
  )
}

export function Switch({ className, ...props }: ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'border-hairline bg-ink-sunken data-[state=checked]:border-blood data-[state=checked]:bg-blood/25 relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center border transition-colors',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="bg-bone-dim data-[state=checked]:bg-blood-bright pointer-events-none block size-4 translate-x-1 transition-transform data-[state=checked]:translate-x-6" />
    </SwitchPrimitive.Root>
  )
}
